import { createSlice } from "@reduxjs/toolkit";
import { apiUserSlice } from "../Services/api.user.slice";
import { removeToken } from "../Utils/tokenStorage";

type UserType = {
    token?: any;
    email?: string;
    propertyList?: any;
    propertyCollections?: any;
    /** "google" | "local" — how this account signs in. */
    provider?: string;
    /**
     * False for an OAuth account whose owner hasn't set a password yet. NOT the
     * same as `provider === "google"`: a Google user who sets one keeps Google,
     * and can then use either.
     */
    hasPassword?: boolean;
    /**
     * Free/premium (October 2026). Display metadata from the login payload —
     * read it through useEntitlements, which also honours the gating switch.
     * Sessions from before this field existed have none and read as free.
     */
    tier?: "free" | "premium";
};

type IsAuthenticatedType = boolean;

type InitialStateType = {
    user: UserType | null;
    isAuthenticated: IsAuthenticatedType;
    toggleAuthModal: boolean;
    isLoading?: boolean;
    wishlist: [];
};

const initialState: InitialStateType = {
    user: {
        propertyList: [],
        propertyCollections: []
    },
    isAuthenticated: false,
    toggleAuthModal: false,
    isLoading: false,
    wishlist: [],
};

type ActionType = {
    payload: any;
};

export const authReducer = createSlice({
    name: "auth",
    initialState,
    reducers: {
        loginSuccess: (state: InitialStateType, action) => {
            state.user = action.payload?.user;
            state.isAuthenticated = true;
            // Token should already be set in localStorage before dispatching this action
            // to ensure it's available when RTK Query's prepareHeaders runs
            return state;
        },
        logout: (state: InitialStateType) => {
            state.user = null;
            state.wishlist = [];
            state.isAuthenticated = false;
            state.toggleAuthModal = false;
            removeToken();
            return state;
        },
        setToggleAuthModal: (state: InitialStateType, action: ActionType) => {
            state.toggleAuthModal = action?.payload;
            return state;
        },
        // Refresh the cached tier from /api/me (after checkout, a grant, or an
        // expiry) so the UI gates unlock/relock in place without a re-login.
        setUserTier: (state: InitialStateType, action: ActionType) => {
            if (state.user) state.user.tier = action?.payload;
            return state;
        },
    },
    extraReducers: (builder) => {
        // builder.addMatcher(apiWishlistSlice.endpoints.getWishlist.matchFulfilled, (state, action) => {
        builder.addMatcher(
            apiUserSlice.endpoints.getWishlist.matchFulfilled,
            (state, action) => {
                state.wishlist = action?.payload?.data || [];
                return state;
            }
        );
        // Optimistically update wishlist on add
        builder.addMatcher(
            apiUserSlice.endpoints.addToWishlist.matchFulfilled,
            (state, action) => {
                const propertyId = String(action?.meta?.arg?.originalArgs?.propertyId || "");
                if (propertyId && !state.wishlist.includes(propertyId as never)) {
                    state.wishlist = [...state.wishlist, propertyId] as any;
                }
                return state;
            }
        );
        // Optimistically update wishlist on remove
        builder.addMatcher(
            apiUserSlice.endpoints.removeFromWishlist.matchFulfilled,
            (state, action) => {
                const propertyId = String(action?.meta?.arg?.originalArgs?.propertyId || "");
                if (propertyId) {
                    state.wishlist = state.wishlist.filter((id: any) => String(id) !== propertyId) as any;
                }
                return state;
            }
        );
        builder.addMatcher(
            apiUserSlice.endpoints.getUserPropertyList.matchFulfilled,
            (state, action) => {
                let idsOnly: [] = action?.payload?.data?.map((item: any) => item?.propertyId?._id);
                state.user!.propertyCollections = action?.payload?.data
                state['user']!['propertyList']! = idsOnly
                return state;
            }
        );
    },
});

export const { loginSuccess, logout, setToggleAuthModal, setUserTier } = authReducer.actions;
export default authReducer.reducer;
